import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Stack, Typography, IconButton, Tooltip, CardMedia, alpha, useTheme, Button, Popover, TextField, ToggleButtonGroup, ToggleButton, Divider, Slider } from '@mui/material';
import { AddRounded, DeleteRounded, EditRounded, FormatColorFillRounded } from '@mui/icons-material';
import Iconify from '../../Iconify';
import { DEFAULT_IMAGE } from '../../../config/config';

/** When already on these, Grid toggle keeps the same column/card layout. */
const GRID_LAYOUT_PRESERVE = ['grid-2', 'grid-3', 'compact', 'image-showcase'];
/** Valid targets when returning List/Feature → Grid (includes featured layout). */
const GRID_LAYOUT_RESTORE = [...GRID_LAYOUT_PRESERVE, 'featured-block'];

const CommonCardGrid = ({
    cards = [],
    variant = 'grid-3',
    /** When switching list/feature → grid, restore this variant (e.g. image-showcase). Set by CommonBlock when leaving a grid layout. */
    lastGridVariant,
    isSiteBuilder = false,
    onUpdateCards,
    onDeleteCard,
    onOpenGallery,
    onUpdateVariant,
    onTriggerPicker,
    blockId,
    editingCardId,
    setEditingCardId
}) => {
    const theme = useTheme();
    const { reducer } = useSelector((state) => state);
    const pageFontFamily = reducer?.pageJson?.pageStyle?.fontFamily;
    /** Variant + inner HTML can override a single root fontFamily — cascade into children too. */
    const pageFontSx =
        pageFontFamily && pageFontFamily !== 'inherit'
            ? {
                fontFamily: `${pageFontFamily} !important`,
                '&, & *': { fontFamily: `${pageFontFamily} !important` },
            }
            : {};

    const [bgColorAnchorEl, setBgColorAnchorEl] = useState(null);
    const [bgColorCardId, setBgColorCardId] = useState(null);
    const [minimalStyleAnchorEl, setMinimalStyleAnchorEl] = useState(null);
    const [numberedStyleAnchorEl, setNumberedStyleAnchorEl] = useState(null);

    const handleCardClick = (event, cardId) => {
        if (!isSiteBuilder) return;
        setEditingCardId(String(cardId));
    };

    const handleClosePopover = () => {
        setEditingCardId(null);
    };
    const isNumbered = variant === 'list-numbered';
    const isCompact = variant === 'compact';
    const isSplitLineList = variant === 'split-line' || variant === 'split-line-list';
    const isSplitLineGrid = variant === 'split-line-grid';
    const isSplitLine = isSplitLineList || isSplitLineGrid;
    const isMinimalLines = variant === 'minimal-lines';
    const isMinimalGrid = variant === 'minimal-grid';
    const isMinimalList = variant === 'minimal-list';
    const isMinimal = isMinimalLines || isMinimalGrid || isMinimalList;
    const isList = variant === 'list';
    const isFeature = variant === 'feature';
    const isFeaturedBlock = variant === 'featured-block';
    const isGridMode = !isList && !isFeature && !isFeaturedBlock;
    const minimalBorderWidth = cards[0]?.minimalBorderWidth || 1;
    const minimalBorderColorValue = cards[0]?.minimalBorderColor || theme.palette.error.main;
    const minimalGridCols = cards[0]?.minimalGridCols || 3;
    const splitLineGridCols = cards[0]?.splitLineGridCols || 3;
    const numberedPreset = cards[0]?.numberedPreset || 'style1';
    const numberedMode = cards[0]?.numberedMode || 'list';
    const numberedGridCols = cards[0]?.numberedGridCols || 3;
    const numberedAccentColor = cards[0]?.numberedAccentColor || '#2d4d2f';
    const numberedNumberColor = cards[0]?.numberedNumberColor || '#ffffff';
    const imageShowcaseCols = cards[0]?.imageShowcaseCols || 4;
    const containerStyle = cards[0]?.containerStyle || 'card';
    const isPlainContainer = containerStyle === 'plain';
    const isMinimalContainer = containerStyle === 'minimal';
    const isNumberedCircleLayout = isNumbered && (numberedPreset === 'style1' || numberedPreset === 'style2');
    const isNumberedStyle1 = isNumbered && numberedPreset === 'style1';
    const isNumberedSideLayout = isNumbered && numberedPreset === 'style3';
    const isNumberedTopHeaderLayout = isNumbered && numberedPreset === 'style4';

    const isSimpleList = variant === 'simple-list';
    const isImageShowcase = variant === 'image-showcase';
    const isSimpleGrid = variant === 'grid-3' || variant === 'grid-2' || isSimpleList || variant === 'featured-block';
    const hasAnyCardImage = cards.some((c) => Boolean(c?.image));
    const isImageFirstVariant = isImageShowcase;
    // Keep normal card layouts text-first; reserve image UI for image variants or cards with explicit images.
    const shouldRenderImage = isImageFirstVariant || isFeaturedBlock || hasAnyCardImage;
    // Ensure image controls are shown only when the image box is shown.
    const shouldShowImageControls = shouldRenderImage;

    let cols = 3;
    if (isList || isFeature || isMinimalList || isMinimalLines || isSplitLineList || isSimpleList || (isNumbered && numberedMode !== 'grid')) cols = 1;
    else if (isImageShowcase) cols = imageShowcaseCols;
    else if (isNumbered && numberedMode === 'grid') cols = numberedGridCols;
    else if (isMinimalGrid) cols = minimalGridCols;
    else if (isSplitLineGrid) cols = splitLineGridCols;
    else if (variant === 'grid-2') cols = 2;
    else if (isFeaturedBlock) cols = 3;
    else if (isCompact || variant === 'image-showcase') cols = 4;

    const handleAddCard = () => {
        onUpdateCards((prevCards) => {
            const baseProps = prevCards.length > 0 ? {
                imageShape: prevCards[0].imageShape,
                imagePosition: prevCards[0].imagePosition,
                cardStyle: prevCards[0].cardStyle,
                containerStyle: prevCards[0].containerStyle,
                minimalBorderWidth: prevCards[0].minimalBorderWidth,
                minimalBorderColor: prevCards[0].minimalBorderColor,
                minimalGridCols: prevCards[0].minimalGridCols,
                splitLineBgColor: prevCards[0].splitLineBgColor,
                splitLineGridCols: prevCards[0].splitLineGridCols,
                numberedPreset: prevCards[0].numberedPreset,
                numberedMode: prevCards[0].numberedMode,
                numberedGridCols: prevCards[0].numberedGridCols,
                numberedAccentColor: prevCards[0].numberedAccentColor,
                numberedNumberColor: prevCards[0].numberedNumberColor,
                imageShowcaseCols: prevCards[0].imageShowcaseCols,
            } : {};
            return [...prevCards, { id: Date.now(), title: 'New Card', description: 'Card description goes here.', ...baseProps }];
        });
    };

    const handleUpdateCard = (id, field, value) => {
        onUpdateCards((prevCards) => prevCards.map(c => String(c.id) === String(id) ? { ...c, [field]: value } : c));
    };
    const isBlurToFloatingToolbar = (event) => {
        const nextFocused = event.relatedTarget;
        return nextFocused instanceof Element && Boolean(nextFocused.closest('[data-floating-text-toolbar="true"]'));
    };
    const normalizeEditableHtml = (html = '') => {
        return String(html)
            .replace(/&nbsp;/g, ' ')
            .replace(/\u00A0/g, ' ')
            .replace(/[ \t]{2,}/g, ' ')
            .replace(/>\s+</g, '><')
            .trim();
    };

    const handleUpdateAllCards = (field, value) => {
        onUpdateCards((prevCards) => prevCards.map(c => ({ ...c, [field]: value })));
    };

    const handleOpenBgColorPicker = (event, cardId) => {
        event.stopPropagation();
        setBgColorAnchorEl(event.currentTarget);
        setBgColorCardId(String(cardId));
    };

    const handleCloseBgColorPicker = () => {
        setBgColorAnchorEl(null);
        setBgColorCardId(null);
    };

    const handleOpenMinimalStylePicker = (event) => {
        event.stopPropagation();
        setMinimalStyleAnchorEl(event.currentTarget);
    };

    const handleCloseMinimalStylePicker = () => {
        setMinimalStyleAnchorEl(null);
    };

    const handleOpenNumberedStylePicker = (event) => {
        event.stopPropagation();
        setNumberedStyleAnchorEl(event.currentTarget);
    };

    const handleCloseNumberedStylePicker = () => {
        setNumberedStyleAnchorEl(null);
    };

    const activeBgCard =
        cards.find((c) => String(c.id) === String(bgColorCardId)) ||
        cards.find((c) => String(c.id) === String(editingCardId)) ||
        cards[0];
    const currentBgColor = isSplitLine
        ? (activeBgCard?.splitLineBgColor || '#f7f1e6')
        : (activeBgCard?.bgColor || '#ffffff');

    return (
        <Box sx={{ width: '100%', my: 2 }}>
            <Grid container spacing={isCompact ? 1 : 3}>
                {cards.map((card, index) => {
                    const useClassicFeaturedLayout = isFeaturedBlock && cards.length >= 2;
                    const isFeaturedLeadCard = useClassicFeaturedLayout && index === 0;
                    const fallbackFeaturedCols = cards.length <= 1 ? 1 : (cards.length === 2 ? 2 : (cards.length === 3 ? 3 : 4));
                    const featuredTopRowSmallCount = Math.min(2, Math.max(cards.length - 1, 0));
                    const featuredRemainingAfterTopRow = Math.max(cards.length - 1 - featuredTopRowSmallCount, 0);
                    const featuredBottomRowMd =
                        featuredRemainingAfterTopRow <= 0 ? 12 :
                            featuredRemainingAfterTopRow === 1 ? 12 :
                                featuredRemainingAfterTopRow === 2 ? 6 :
                                    featuredRemainingAfterTopRow === 3 ? 4 : 3;
                    const gridMd = isFeature
                        ? 12
                        : (isFeaturedBlock
                            ? (useClassicFeaturedLayout
                                ? (isFeaturedLeadCard
                                    ? 6
                                    : (index <= 2 ? 3 : featuredBottomRowMd))
                                : (12 / fallbackFeaturedCols))
                            : (isList ? 12 : (12 / cols)));
                    const isCardSelected = isSiteBuilder && String(editingCardId) === String(card.id);
                    return (
                        <Grid item xs={12} sm={isFeature || isList || isSimpleList || isFeaturedBlock ? 12 : (cols === 1 ? 12 : 6)} md={gridMd} key={card.id}>
                            <Box
                                onClick={(e) => handleCardClick(e, card.id)}
                                className="card-root"
                                sx={{
                                    position: 'relative',
                                    height: '100%',
                                    overflow: 'hidden',
                                    p: card.padding !== undefined
                                        ? `${card.padding}px`
                                        : (isPlainContainer
                                            ? 0
                                            : (isMinimal ? 0 : (isFeature ? 5 : (isCompact ? 1.5 : (isFeaturedBlock ? (isFeaturedLeadCard ? 4 : 3) : 3))))),
                                    borderRadius: isSplitLine
                                        ? (card.borderRadius !== undefined ? `${card.borderRadius}px` : '36px 0 36px 0')
                                        : (card.borderRadius !== undefined ? `${card.borderRadius}px` : (isPlainContainer ? 0 : 2)),
                                    bgcolor: isPlainContainer
                                        ? 'transparent'
                                        : (isMinimalContainer
                                            ? alpha(theme.palette.background.paper, 0.35)
                                            : (isSplitLine
                                        ? (card.splitLineBgColor || '#f7f1e6')
                                        : (card.bgColor || (isNumbered ? '#f7f1e6' : (isSimpleGrid || isFeaturedBlock ? alpha(theme.palette.background.paper, 0.9) : 'transparent'))))),
                                    color: card.textColor || 'text.primary',
                                    border: isPlainContainer
                                        ? (isCardSelected ? `2px solid ${theme.palette.primary.main}` : 'none')
                                        : (isMinimal
                                        ? 'none'
                                        : (isSplitLine
                                            ? 'none'
                                            : (isNumbered
                                                ? (isCardSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid')
                                                : (isCardSelected ? `3px solid ${theme.palette.primary.main}` : '1px solid')))),
                                    borderTop: (isPlainContainer || isMinimal || isNumbered || isFeature) ? 'none' : '1px solid',
                                    borderBottom: isSplitLine ? undefined : undefined,
                                    borderLeft: isMinimal
                                        ? `${Math.max(4, minimalBorderWidth * 2)}px solid ${minimalBorderColorValue}`
                                        : undefined,
                                    borderColor: isMinimal
                                        ? undefined
                                        : (isSplitLine ? undefined : (isCardSelected ? theme.palette.primary.main : 'divider')),
                                    borderLeftColor: isMinimal ? minimalBorderColorValue : undefined,
                                    boxShadow: isPlainContainer
                                        ? 'none'
                                        : (isFeature
                                        ? '0 10px 30px -10px rgba(0,0,0,0.5)'
                                        : (isMinimal
                                            ? (isCardSelected
                                                ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}, 0 8px 20px rgba(0,0,0,0.08)`
                                                : '0 6px 16px rgba(0,0,0,0.06)')
                                            : (isSplitLine ? 'none' : (isCardSelected ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}` : (isSimpleGrid ? '0 2px 8px rgba(0,0,0,0.06)' : 3))))),
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: isSiteBuilder ? 'pointer' : 'default',
                                    width: isSplitLine ? '100%' : '100%',
                                    mx: isSplitLine ? 0 : undefined,
                                    py: isMinimal ? 2.5 : (isSplitLine ? 3 : undefined),
                                    px: isMinimal ? 2 : (isSplitLine ? 5 : undefined),
                                    pt: isNumberedCircleLayout ? 5 : undefined,
                                    minHeight: isFeaturedBlock
                                        ? (isFeaturedLeadCard ? 420 : 200)
                                        : (isNumberedStyle1 ? 170 : (isNumberedTopHeaderLayout ? 220 : undefined)),
                                    '&:hover': isSiteBuilder ? {} : {
                                        transform: isFeature ? 'translateY(-8px)' : 'none',
                                        boxShadow: isFeature ? '0 20px 40px -15px rgba(0,0,0,0.6)' : undefined
                                    }
                                }}
                            >
                                {isNumberedCircleLayout && numberedPreset === 'style1' && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 34,
                                            left: 0,
                                            right: 0,
                                            height: 6,
                                            bgcolor: numberedAccentColor
                                        }}
                                    />
                                )}

                                {isNumberedCircleLayout && numberedPreset === 'style1' && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 6,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 58,
                                            height: 58,
                                            borderRadius: '50%',
                                            bgcolor: numberedAccentColor,
                                            color: numberedNumberColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '2rem',
                                            fontWeight: 700,
                                            zIndex: 1
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                )}

                                {isSiteBuilder && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteCard(card.id);
                                            if (String(editingCardId) === String(card.id)) {
                                                handleClosePopover();
                                            }
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 2,
                                            bgcolor: alpha(theme.palette.background.paper, 0.9),
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            '&:hover': { bgcolor: 'background.paper' },
                                            '.card-root:hover &': { opacity: 1 }
                                        }}
                                    >
                                        <DeleteRounded fontSize="small" />
                                    </IconButton>
                                )}

                                <Stack
                                    direction={
                                        card.imagePosition === 'top' ? 'column' :
                                            card.imagePosition === 'bottom' ? 'column-reverse' :
                                                card.imagePosition === 'right' ? 'row-reverse' :
                                                    card.imagePosition === 'left' ? 'row' :
                                                        (isSplitLine ? 'column' : ((isList || isSimpleList || isNumberedSideLayout) ? 'row' : 'column'))
                                    }
                                    spacing={isFeature ? 4 : (isSplitLine ? 4 : 2)}
                                    alignItems={isSplitLine ? 'center' : ((isList || isSimpleList || card.imagePosition === 'left' || card.imagePosition === 'right' || isNumberedSideLayout || isFeature || isNumberedCircleLayout || isNumberedTopHeaderLayout) ? 'center' : 'stretch')}
                                    justifyContent={isNumberedStyle1 ? 'flex-start' : (isSplitLine ? 'center' : ((isList || isSimpleList || card.imagePosition === 'left' || card.imagePosition === 'right' || isNumberedSideLayout || isFeature || isNumberedCircleLayout || isNumberedTopHeaderLayout) ? 'center' : 'flex-start'))}
                                    sx={{ height: '100%', width: '100%', pt: isNumberedStyle1 ? 6 : 0, position: 'relative', zIndex: 2 }}
                                >
                                    {shouldRenderImage && (
                                        <Box sx={{
                                            width: (card.imagePosition === 'left' || card.imagePosition === 'right' || (!card.imagePosition && (isList || isSimpleList || isNumbered))) ?
                                                (isFeature ? 200 : (cols === 1 ? 160 : 120)) :
                                                ((card.imageShape === 'round' || cards[0]?.imageShape === 'round') ? 160 : '100%'),
                                            mx: (card.imagePosition === 'left' || card.imagePosition === 'right' || (!card.imagePosition && (isList || isSimpleList || isNumbered))) ? 0 : 'auto',
                                            flexShrink: 0,
                                            position: 'relative',
                                            pb: (card.imagePosition === 'left' || card.imagePosition === 'right' || (!card.imagePosition && (isList || isSimpleList || isNumbered))) ? 0 :
                                                ((card.imageShape === 'round' || cards[0]?.imageShape === 'round') ? '160px' : '75%'),
                                            height: (card.imagePosition === 'left' || card.imagePosition === 'right' || (!card.imagePosition && (isList || isSimpleList || isNumbered))) ?
                                                (isFeature ? 200 : (cols === 1 ? 160 : 120)) :
                                                ((card.imageShape === 'round' || cards[0]?.imageShape === 'round') ? 160 : 'auto'),
                                            borderRadius: (card.imageShape === 'round' || cards[0]?.imageShape === 'round') ? '50%' : 1,
                                            overflow: 'hidden',
                                            bgcolor: alpha(theme.palette.divider, 0.05),
                                            cursor: isSiteBuilder ? 'pointer' : 'default',
                                            ...((card.imagePosition === 'left' || (!card.imagePosition && (isList || isSimpleList || isNumbered))) ? { mr: 2 } : {}),
                                            '&:hover .image-edit-overlay': { opacity: 1 }
                                        }}
                                            onClick={() => {
                                                if (isSiteBuilder) {
                                                    setEditingCardId(String(card.id));
                                                    onOpenGallery(card.id);
                                                }
                                            }}>
                                            {card.image ? (
                                                <CardMedia
                                                    component="img"
                                                    image={card.image}
                                                    alt={card.title}
                                                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <CardMedia
                                                    component="img"
                                                    image={DEFAULT_IMAGE}
                                                    alt="Default"
                                                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }}
                                                />
                                            )}
                                            {isSiteBuilder && (
                                                <Box className="image-edit-overlay" sx={{
                                                    position: 'absolute', inset: 0, bgcolor: alpha(theme.palette.common.black, 0.4),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: '0.2s'
                                                }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<EditRounded />}
                                                sx={{ fontSize: '0.65rem' }}
                                            >
                                                {card.image ? 'Change' : 'Add'}
                                            </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {isNumberedCircleLayout && numberedPreset !== 'style1' && (
                                        <Box
                                            sx={{
                                                width: 58,
                                                height: 58,
                                                borderRadius: '50%',
                                                bgcolor: numberedAccentColor,
                                                color: numberedNumberColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '2rem',
                                                fontWeight: 700,
                                                mb: 1.5
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                    )}

                                    {isNumberedSideLayout && (
                                        <Box
                                            sx={{
                                                minWidth: 72,
                                                alignSelf: 'stretch',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '2.2rem',
                                                fontWeight: 600,
                                                borderRight: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                                                bgcolor: numberedPreset === 'style4' ? alpha(numberedAccentColor, 0.12) : 'transparent'
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                    )}

                                    {isNumberedTopHeaderLayout && (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                py: 1.25,
                                                mb: 1,
                                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                                                textAlign: 'center',
                                                fontSize: '2rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                    )}

                                    <Stack spacing={1} sx={{ flex: 1, textAlign: (isSplitLine || isNumberedCircleLayout || isNumberedTopHeaderLayout) ? 'center' : 'left' }}>
                                        <Typography
                                            contentEditable={isSiteBuilder}
                                            suppressContentEditableWarning
                                            onFocus={() => isSiteBuilder && setEditingCardId(String(card.id))}
                                            onBlur={(e) => {
                                                if (isBlurToFloatingToolbar(e)) return;
                                                handleUpdateCard(card.id, 'title', normalizeEditableHtml(e.target.innerHTML));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    // Keep editing in title and insert a line break.
                                                    document.execCommand('insertLineBreak');
                                                } else if (e.key === '/' && !e.currentTarget.innerText.trim() && onTriggerPicker) {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    onTriggerPicker(rect, blockId);
                                                }
                                            }}
                                            variant={isCompact ? 'subtitle1' : 'h6'}
                                            fontWeight={700}
                                            dangerouslySetInnerHTML={{ __html: card.title || '' }}
                                            className="card-title"
                                            sx={{
                                                outline: 'none',
                                                cursor: isSiteBuilder ? 'text' : 'inherit',
                                                borderRadius: 0.5,
                                                px: 0.5,
                                                mx: -0.5,
                                                color: 'inherit',
                                                maxWidth: '100%',
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                '&:hover': isSiteBuilder ? { bgcolor: alpha(theme.palette.action.hover, 0.05) } : {},
                                                ...pageFontSx,
                                            }}
                                        />
                                        {isSiteBuilder && (isFeaturedBlock || isFeature) && card.image && (
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="text"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleUpdateCard(card.id, 'image', null);
                                                }}
                                                sx={{ textTransform: 'none', alignSelf: 'flex-start', px: 0.5 }}
                                            >
                                                Remove image
                                            </Button>
                                        )}
                                        {!isSplitLine && (!isNumbered || isNumberedStyle1) && (
                                            <Typography
                                                contentEditable={isSiteBuilder}
                                                suppressContentEditableWarning
                                                onFocus={() => isSiteBuilder && setEditingCardId(String(card.id))}
                                                onBlur={(e) => {
                                                    if (isBlurToFloatingToolbar(e)) return;
                                                    handleUpdateCard(card.id, 'description', normalizeEditableHtml(e.target.innerHTML));
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        // Keep editing in description and insert a line break.
                                                        document.execCommand('insertLineBreak');
                                                    } else if (e.key === 'Backspace' && !e.currentTarget.innerText.trim() && cards.length > 1) {
                                                        e.preventDefault();
                                                        onDeleteCard(card.id);
                                                    } else if (e.key === '/' && !e.currentTarget.innerText.trim() && onTriggerPicker) {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        onTriggerPicker(rect, blockId);
                                                    }
                                                }}
                                                variant="body2"
                                                color="inherit"
                                                dangerouslySetInnerHTML={{ __html: card.description || '' }}
                                                className="card-description"
                                                sx={{
                                                    outline: 'none',
                                                    cursor: isSiteBuilder ? 'text' : 'inherit',
                                                    opacity: 0.85,
                                                    borderRadius: 0.5,
                                                    px: 0.5,
                                                    mx: -0.5,
                                                    maxWidth: '100%',
                                                    overflowWrap: 'anywhere',
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'pre-wrap',
                                                    '&:hover': isSiteBuilder ? { bgcolor: alpha(theme.palette.action.hover, 0.05) } : {},
                                                    ...pageFontSx,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                            </Box>

                            {/* Card Properties Toolbar (Absolutely positioned above the card) */}
                            {isSiteBuilder && String(editingCardId) === String(card.id) && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -10,
                                        left: '50%',
                                        transform: 'translate(-50%, -100%)',
                                        zIndex: 1000,
                                        p: 0.5,
                                        bgcolor: 'background.paper',
                                        boxShadow: theme.customShadows?.z24 || 20,
                                        borderRadius: 1,
                                        border: `1px solid ${theme.palette.divider}`,
                                        whiteSpace: 'nowrap',
                                        '&:after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -6,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            borderLeft: '6px solid transparent',
                                            borderRight: '6px solid transparent',
                                            borderTop: `6px solid ${theme.palette.background.paper}`,
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ p: 0.5 }}>
                                        {isMinimal ? (
                                            <>
                                                <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                    <ToggleButtonGroup
                                                        size="small"
                                                        value={isMinimalGrid ? 'grid' : 'list'}
                                                        exclusive
                                                        onChange={(e, next) => {
                                                            if (!next) return;
                                                            onUpdateVariant(next === 'grid' ? 'minimal-grid' : 'minimal-lines');
                                                        }}
                                                    >
                                                        <ToggleButton value="grid"><Tooltip title="Grid Layout"><Iconify icon="eva:grid-fill" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="list"><Tooltip title="Column Layout"><Iconify icon="eva:list-fill" /></Tooltip></ToggleButton>
                                                    </ToggleButtonGroup>
                                                </Stack>

                                                {isMinimalGrid && (
                                                    <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                        <ToggleButtonGroup
                                                            size="small"
                                                            value={String(minimalGridCols)}
                                                            exclusive
                                                            onChange={(e, next) => next && handleUpdateAllCards('minimalGridCols', Number(next))}
                                                        >
                                                            <ToggleButton value="2"><Tooltip title="2 columns"><Typography variant="caption" fontWeight={700}>2</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="3"><Tooltip title="3 columns"><Typography variant="caption" fontWeight={700}>3</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="4"><Tooltip title="4 columns"><Typography variant="caption" fontWeight={700}>4</Typography></Tooltip></ToggleButton>
                                                        </ToggleButtonGroup>
                                                    </Stack>
                                                )}

                                                <Tooltip title="Minimal border settings">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleOpenMinimalStylePicker}
                                                        sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 1, mr: 0.5, borderRadius: 0 }}
                                                    >
                                                        <Iconify icon="eva:options-2-fill" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        ) : isSplitLine ? (
                                            <>
                                                <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                    <ToggleButtonGroup
                                                        size="small"
                                                        value={isSplitLineGrid ? 'grid' : 'list'}
                                                        exclusive
                                                        onChange={(e, next) => {
                                                            if (!next) return;
                                                            onUpdateVariant(next === 'grid' ? 'split-line-grid' : 'split-line-list');
                                                        }}
                                                    >
                                                        <ToggleButton value="grid"><Tooltip title="Grid Layout"><Iconify icon="eva:grid-fill" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="list"><Tooltip title="Column Layout"><Iconify icon="eva:list-fill" /></Tooltip></ToggleButton>
                                                    </ToggleButtonGroup>
                                                </Stack>

                                                {isSplitLineGrid && (
                                                    <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                        <ToggleButtonGroup
                                                            size="small"
                                                            value={String(splitLineGridCols)}
                                                            exclusive
                                                            onChange={(e, next) => next && handleUpdateAllCards('splitLineGridCols', Number(next))}
                                                        >
                                                            <ToggleButton value="2"><Tooltip title="2 columns"><Typography variant="caption" fontWeight={700}>2</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="3"><Tooltip title="3 columns"><Typography variant="caption" fontWeight={700}>3</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="4"><Tooltip title="4 columns"><Typography variant="caption" fontWeight={700}>4</Typography></Tooltip></ToggleButton>
                                                        </ToggleButtonGroup>
                                                    </Stack>
                                                )}
                                            </>
                                        ) : isNumbered ? (
                                            <>
                                                <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                    <ToggleButtonGroup
                                                        size="small"
                                                        value={numberedMode}
                                                        exclusive
                                                        onChange={(e, next) => next && handleUpdateAllCards('numberedMode', next)}
                                                    >
                                                        <ToggleButton value="grid"><Tooltip title="Grid Layout"><Iconify icon="eva:grid-fill" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="list"><Tooltip title="Column Layout"><Iconify icon="eva:list-fill" /></Tooltip></ToggleButton>
                                                    </ToggleButtonGroup>
                                                </Stack>

                                                {numberedMode === 'grid' && (
                                                    <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                        <ToggleButtonGroup
                                                            size="small"
                                                            value={String(numberedGridCols)}
                                                            exclusive
                                                            onChange={(e, next) => next && handleUpdateAllCards('numberedGridCols', Number(next))}
                                                        >
                                                            <ToggleButton value="2"><Tooltip title="2 columns"><Typography variant="caption" fontWeight={700}>2</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="3"><Tooltip title="3 columns"><Typography variant="caption" fontWeight={700}>3</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="4"><Tooltip title="4 columns"><Typography variant="caption" fontWeight={700}>4</Typography></Tooltip></ToggleButton>
                                                        </ToggleButtonGroup>
                                                    </Stack>
                                                )}

                                                <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                    <ToggleButtonGroup
                                                        size="small"
                                                        value={numberedPreset}
                                                        exclusive
                                                        onChange={(e, next) => next && handleUpdateAllCards('numberedPreset', next)}
                                                    >
                                                        <ToggleButton value="style1"><Tooltip title="Top circle + line"><Iconify icon="mdi:minus-circle-outline" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="style2"><Tooltip title="Top circle"><Iconify icon="mdi:checkbox-blank-circle-outline" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="style3"><Tooltip title="Side number column"><Iconify icon="mdi:view-day-outline" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="style4"><Tooltip title="Side shaded number column"><Iconify icon="mdi:view-column-outline" /></Tooltip></ToggleButton>
                                                    </ToggleButtonGroup>
                                                </Stack>

                                                <Tooltip title="Numbered style colors">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleOpenNumberedStylePicker}
                                                        sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 1, mr: 0.5, borderRadius: 0 }}
                                                    >
                                                        <Iconify icon="eva:color-palette-fill" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (isSimpleGrid && !isFeaturedBlock) ? (
                                            <>
                                                {/* Simple Card Grid: list/grid toggle */}
                                                <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                    <ToggleButtonGroup
                                                        size="small"
                                                        value={isFeaturedBlock ? 'featured-block' : (isSimpleList ? 'list' : 'grid')}
                                                        exclusive
                                                        onChange={(e, next) => {
                                                            if (!next) return;
                                                            if (next === 'featured-block') {
                                                                onUpdateVariant('featured-block');
                                                                return;
                                                            }
                                                            onUpdateVariant(next === 'list' ? 'simple-list' : 'grid-3');
                                                        }}
                                                    >
                                                        <ToggleButton value="grid"><Tooltip title="Grid Layout"><Iconify icon="eva:grid-fill" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="list"><Tooltip title="List Layout"><Iconify icon="eva:list-fill" /></Tooltip></ToggleButton>
                                                        <ToggleButton value="featured-block"><Tooltip title="Featured Block"><Iconify icon="eva:layout-fill" /></Tooltip></ToggleButton>
                                                    </ToggleButtonGroup>
                                                </Stack>

                                                {/* Column count — only in grid mode */}
                                                {!isSimpleList && (
                                                    <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                        <ToggleButtonGroup
                                                            size="small"
                                                            value={variant === 'grid-2' ? '2' : '3'}
                                                            exclusive
                                                            onChange={(e, next) => {
                                                                if (!next) return;
                                                                onUpdateVariant(next === '2' ? 'grid-2' : 'grid-3');
                                                            }}
                                                        >
                                                            <ToggleButton value="2"><Tooltip title="2 columns"><Typography variant="caption" fontWeight={700}>2</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="3"><Tooltip title="3 columns"><Typography variant="caption" fontWeight={700}>3</Typography></Tooltip></ToggleButton>
                                                        </ToggleButtonGroup>
                                                    </Stack>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {/* Card Style Group (Switcher between Standard, List, Feature) */}
                                                <ToggleButtonGroup
                                                    size="small"
                                                value={isImageShowcase ? 'image-showcase' : (isFeaturedBlock ? 'featured-block' : (isFeature ? 'feature' : (isList ? 'list' : 'grid')))}
                                                    exclusive
                                                        onChange={(e, next) => {
                                                        if (!next) return;
                                                        if (isImageShowcase) {
                                                            if (next === 'list') {
                                                                handleUpdateAllCards('imagePosition', 'left');
                                                                handleUpdateAllCards('imageShape', 'square');
                                                                onUpdateVariant('image-showcase');
                                                                return;
                                                            }
                                                            if (next === 'grid') {
                                                                handleUpdateAllCards('imagePosition', 'top');
                                                                handleUpdateAllCards('imageShape', 'square');
                                                                onUpdateVariant('image-showcase');
                                                                return;
                                                            }
                                                            if (next === 'featured-block' || next === 'image-showcase') {
                                                                onUpdateVariant('featured-block');
                                                                return;
                                                            }
                                                        }
                                                        const nextVariant = next === 'grid'
                                                            ? (GRID_LAYOUT_PRESERVE.includes(variant)
                                                                ? variant
                                                                : (lastGridVariant && GRID_LAYOUT_RESTORE.includes(lastGridVariant)
                                                                    ? lastGridVariant
                                                                    : 'grid-3'))
                                                            : (next === 'list' && isFeaturedBlock)
                                                                ? 'image-showcase'
                                                            : (next === 'list' && isFeature)
                                                                ? 'simple-list'
                                                                : next;
                                                        if (typeof window !== 'undefined' && window.localStorage?.getItem('siteBuilderDebug') === '1') {
                                                            console.log('[siteBuilderDebug][CommonCardGrid] variantToggle', {
                                                                cardId: card.id,
                                                                from: variant,
                                                                to: nextVariant,
                                                                lastGridVariant
                                                            });
                                                        }
                                                        onUpdateVariant(nextVariant);
                                                    }}
                                                    sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}
                                                >
                                                    <ToggleButton value="grid"><Tooltip title="Grid Layout"><Iconify icon="eva:grid-fill" /></Tooltip></ToggleButton>
                                                    <ToggleButton value="list"><Tooltip title="Column Layout"><Iconify icon="eva:list-fill" /></Tooltip></ToggleButton>
                                                    {isImageShowcase ? (
                                                        <ToggleButton value="featured-block"><Tooltip title="Featured Block"><Iconify icon="eva:layout-fill" /></Tooltip></ToggleButton>
                                                    ) : (
                                                    <ToggleButton value="featured-block"><Tooltip title="Featured Block"><Iconify icon="eva:layout-fill" /></Tooltip></ToggleButton>
                                                    )}
                                                </ToggleButtonGroup>

                                                {(isGridMode || isImageShowcase) && (
                                                    <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                        <ToggleButtonGroup
                                                            size="small"
                                                            value={isImageShowcase ? String(imageShowcaseCols) : (variant === 'grid-2' ? '2' : (variant === 'compact' ? '4' : '3'))}
                                                            exclusive
                                                            onChange={(e, next) => {
                                                                if (!next) return;
                                                                if (isImageShowcase) {
                                                                    handleUpdateAllCards('imageShowcaseCols', Number(next));
                                                                    onUpdateVariant('image-showcase');
                                                                    return;
                                                                }
                                                                const nextVariant = next === '2' ? 'grid-2' : (next === '4' ? 'compact' : 'grid-3');
                                                                onUpdateVariant(nextVariant);
                                                            }}
                                                        >
                                                            <ToggleButton value="2"><Tooltip title="2 columns"><Typography variant="caption" fontWeight={700}>2</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="3"><Tooltip title="3 columns"><Typography variant="caption" fontWeight={700}>3</Typography></Tooltip></ToggleButton>
                                                            <ToggleButton value="4"><Tooltip title="4 columns"><Typography variant="caption" fontWeight={700}>4</Typography></Tooltip></ToggleButton>
                                                        </ToggleButtonGroup>
                                                    </Stack>
                                                )}

                                                {/* Image Styling Group */}
                                                {shouldShowImageControls && (
                                                    <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                                        <ToggleButtonGroup
                                                            size="small"
                                                            value={cards[0]?.imageShape || 'square'}
                                                            exclusive
                                                            onChange={(e, next) => next !== null && handleUpdateAllCards('imageShape', next)}
                                                        >
                                                            <ToggleButton value="square"><Tooltip title="Rectangle Image"><Iconify icon="eva:square-fill" /></Tooltip></ToggleButton>
                                                            <ToggleButton value="round"><Tooltip title="Circle Image"><Iconify icon="eva:radio-button-off-fill" /></Tooltip></ToggleButton>
                                                        </ToggleButtonGroup>

                                                        <ToggleButtonGroup
                                                            size="small"
                                                            value={cards[0]?.imagePosition || 'top'}
                                                            exclusive
                                                            onChange={(e, next) => {
                                                                if (next === null) return;
                                                                if (typeof window !== 'undefined' && window.localStorage?.getItem('siteBuilderDebug') === '1') {
                                                                    console.log('[siteBuilderDebug][CommonCardGrid] imagePositionToggle', {
                                                                        cardId: card.id,
                                                                        from: cards[0]?.imagePosition || 'top',
                                                                        to: next
                                                                    });
                                                                }
                                                                handleUpdateAllCards('imagePosition', next);
                                                            }}
                                                        >
                                                            <ToggleButton value="top"><Tooltip title="Image Top"><Iconify icon="eva:arrow-up-fill" /></Tooltip></ToggleButton>
                                                            <ToggleButton value="left"><Tooltip title="Image Left"><Iconify icon="eva:arrow-left-fill" /></Tooltip></ToggleButton>
                                                            <ToggleButton value="right"><Tooltip title="Image Right"><Iconify icon="eva:arrow-right-fill" /></Tooltip></ToggleButton>
                                                        </ToggleButtonGroup>
                                                    </Stack>
                                                )}

                                            </>
                                        )}

                                        <Stack direction="row" spacing={0.5} sx={{ borderRight: '1px solid', borderColor: 'divider', pr: 0.5, mr: 0.5 }}>
                                            <ToggleButtonGroup
                                                size="small"
                                                value={containerStyle}
                                                exclusive
                                                onChange={(e, next) => next && handleUpdateAllCards('containerStyle', next)}
                                            >
                                                <ToggleButton value="card"><Tooltip title="Card container"><Typography variant="caption" fontWeight={700}>C</Typography></Tooltip></ToggleButton>
                                                <ToggleButton value="plain"><Tooltip title="Plain (no card)"><Typography variant="caption" fontWeight={700}>P</Typography></Tooltip></ToggleButton>
                                            </ToggleButtonGroup>
                                        </Stack>

                                        {/* Link & Settings Group */}
                                        <Tooltip title="Background Color">
                                            <IconButton size="small" onClick={(e) => handleOpenBgColorPicker(e, card.id)}>
                                                <FormatColorFillRounded fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                        <IconButton size="small" onClick={handleClosePopover}>
                                            <Iconify icon="eva:close-fill" />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            )}
                        </Grid>
                    );
                })}

                {/* Add Card Placeholder */}
                {isSiteBuilder && (
                    <Grid item xs={12} sm={cols === 1 ? 12 : 6} md={12 / cols}>
                        <Box
                            onClick={handleAddCard}
                            sx={{
                                height: '100%',
                                minHeight: 120,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 2,

                                cursor: 'pointer',
                                color: 'text.secondary',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    bgcolor: 'rgba(0,0,0,0.02)'
                                }
                            }}
                        >
                            <AddRounded />
                            <Typography variant="caption" sx={{ mt: 1 }}>Add Card</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Popover
                open={Boolean(bgColorAnchorEl)}
                anchorEl={bgColorAnchorEl}
                onClose={handleCloseBgColorPicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 180 }} onClick={(e) => e.stopPropagation()}>
                    <Typography variant="caption" color="text.secondary">Card Background</Typography>
                    <TextField
                        size="small"
                        type="color"
                        value={currentBgColor}
                        onChange={(e) => {
                            const field = isSplitLine ? 'splitLineBgColor' : 'bgColor';
                            const targetId = bgColorCardId || editingCardId;
                            if (targetId) {
                                handleUpdateCard(targetId, field, e.target.value);
                            } else {
                                handleUpdateAllCards(field, e.target.value);
                            }
                        }}
                        inputProps={{ 'aria-label': 'Card background color' }}
                        sx={{ width: '100%' }}
                    />
                </Stack>
            </Popover>

            <Popover
                open={Boolean(minimalStyleAnchorEl)}
                anchorEl={minimalStyleAnchorEl}
                onClose={handleCloseMinimalStylePicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                    <Typography variant="caption" color="text.secondary">Minimal Border Width</Typography>
                    <Slider
                        size="small"
                        value={Number(minimalBorderWidth) || 1}
                        min={1}
                        max={12}
                        step={1}
                        valueLabelDisplay="auto"
                        onChange={(e, value) => {
                            const next = Array.isArray(value) ? value[0] : value;
                            handleUpdateAllCards('minimalBorderWidth', Number(next));
                        }}
                    />

                    <Typography variant="caption" color="text.secondary">Minimal Border Color</Typography>
                    <TextField
                        size="small"
                        type="color"
                        value={minimalBorderColorValue}
                        onChange={(e) => handleUpdateAllCards('minimalBorderColor', e.target.value)}
                        inputProps={{ 'aria-label': 'Minimal border color' }}
                        sx={{ width: '100%' }}
                    />
                </Stack>
            </Popover>

            <Popover
                open={Boolean(numberedStyleAnchorEl)}
                anchorEl={numberedStyleAnchorEl}
                onClose={handleCloseNumberedStylePicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                    <Typography variant="caption" color="text.secondary">Number Accent Color</Typography>
                    <TextField
                        size="small"
                        type="color"
                        value={numberedAccentColor}
                        onChange={(e) => handleUpdateAllCards('numberedAccentColor', e.target.value)}
                        inputProps={{ 'aria-label': 'Numbered accent color' }}
                        sx={{ width: '100%' }}
                    />

                    <Typography variant="caption" color="text.secondary">Number Text Color</Typography>
                    <TextField
                        size="small"
                        type="color"
                        value={numberedNumberColor}
                        onChange={(e) => handleUpdateAllCards('numberedNumberColor', e.target.value)}
                        inputProps={{ 'aria-label': 'Numbered number color' }}
                        sx={{ width: '100%' }}
                    />
                </Stack>
            </Popover>

        </Box>
    );
};

export default CommonCardGrid;
